import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    AirspaceCheckCommand,
    AirspaceJurisdictionDto,
    DEFAULT_MAX_HEIGHT,
    FlyFreelyLoggingService,
    fromTimestamp,
    mutateState,
    nullSafeDistinctUntilKeyChanged,
    patchableState,
    patchState,
    RulesetDto,
    StateUpdateCommand,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import { deepEqual } from 'fast-equals';
import { Point, Polygon } from 'geojson';
import moment from 'moment';
import {
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    ReplaySubject,
    shareReplay,
    Subject,
    timer,
    withLatestFrom
} from 'rxjs';
import { AirspaceCheckService } from '../airspace-check.service';

export interface AirspaceCheckerParameters {
    /** The absolute timestamp */
    airspaceDate: Date;
    /**
     * The duration in seconds
     */
    airspaceDuration: number;
    airspaceRuleset: RulesetDto;
    airspaceHeight: number;
    /**
     * The timezone to project airspaceDate into for display
     */
    airspaceTimeZone: string;
    radius?: number;
}

interface DeferredParameters {
    rulesetIdentifier?: string;
}

type AirspaceCheckCommandWithoutLocation = Omit<
    AirspaceCheckCommand,
    'location' | 'jurisdiction'
>;

/**
 * Updates the airspace checker command and runs the check.
 * This should be triggered whenever any checker-relevant values change.
 * The arguments express just the required values.
 */
function computeAirspaceCheckCommand(parameters: {
    airspaceDate: Date | null;
    airspaceHeight: number | null;
    airspaceDuration: number | null;
    airspaceRuleset: RulesetDto | null;
}): AirspaceCheckCommandWithoutLocation | null {
    if (
        parameters.airspaceHeight == null ||
        parameters.airspaceDate == null ||
        parameters.airspaceDuration == null ||
        parameters.airspaceDuration < 1 ||
        parameters.airspaceDuration > 82800 ||
        parameters.airspaceRuleset == null
    ) {
        return null;
    }

    const startTime = parameters.airspaceDate.toISOString();

    const endTime = moment(startTime)
        .add(parameters.airspaceDuration, 'seconds')
        .toISOString();

    const airspaceCheckCommand: AirspaceCheckCommandWithoutLocation = {
        ruleset: parameters.airspaceRuleset.identifier,
        maximumHeight: parameters.airspaceHeight,
        startTime: startTime,
        endTime: endTime,
        nearestFeatureSearch: false,
        nearestFeatureSearchRadiusMeters: 400,
        rpaList: [] // this.airspaceCheckCommand?.rpaList ?? []
    };

    return airspaceCheckCommand;
}

/**
 * This service coordinates all the components in this directory.
 *
 * The collaborating AirspaceCheckService is the system of record for
 * some properties, such as jurisdiction.
 */
@Injectable()
export class MapAirspaceCheckerService {
    private organisationId: number | undefined;

    private deferredParameters: DeferredParameters;

    private isInPastSource = new ReplaySubject<boolean>();
    isInPast$ = this.isInPastSource.asObservable();

    /**
     * The current airspace parameters that have been set by the user or the initial values.
     * This is the system of record for this information.
     */

    private currentStateSource = new Subject<
        StateUpdateCommand<{
            flightArea: GeoJSON.Polygon | GeoJSON.Point | null;
            automaticTime: boolean;
            parameters: Partial<AirspaceCheckerParameters>;
        }>
    >();
    private currentState$ = patchableState(this.currentStateSource, {
        flightArea: null,
        automaticTime: true,
        parameters: {
            airspaceDate: new Date(),
            airspaceDuration: 3600,
            airspaceHeight: DEFAULT_MAX_HEIGHT,
            radius: 100,
            airspaceTimeZone: null,
            airspaceRuleset: null
        }
    });

    currentParameters$ = this.currentState$.pipe(
        map(s => s.parameters),
        distinctUntilChanged()
    );

    /**
     * Subset of parameters needed for time-based map functions (LOTAMS, neighbouring missions).
     */
    timingParameters$ = this.currentState$.pipe(
        map(s => ({
            airspaceDate: s.parameters.airspaceDate,
            airspaceDuration: s.parameters.airspaceDuration,
            airspaceTimeZone: s.parameters.airspaceTimeZone
        })),
        distinctUntilChanged(deepEqual),
        filter(
            p =>
                p.airspaceDate != null &&
                p.airspaceDuration != null &&
                p.airspaceDuration >= 1 &&
                p.airspaceDuration <= 82800
        )
    );

    /**
     * This is a duplicate of the validation that happens in the entry form in `map-airspace-parameters.component.ts`.
     */
    parameterValidity$ = combineLatest([
        this.currentParameters$,
        timer(0, 30000)
    ]).pipe(
        map(([p]) => ({
            airspaceDuration:
                p.airspaceDuration != null &&
                p.airspaceDuration >= 1 &&
                p.airspaceDuration <= 82800,
            airspaceRuleset: p.airspaceRuleset != null,
            airspaceHeight: p.airspaceHeight != null,
            airspaceTimeZone: p.airspaceTimeZone != null,
            airspaceDate: moment(p.airspaceDate).diff(moment(), 'm') >= 0
        })),
        shareReplay()
    );

    /**
     * The Airspace Jurisdiction from the checker service, but distinct when the
     * jurisdiction doesn't change.
     */
    airspaceJurisdiction$: typeof AirspaceCheckService.prototype.airspaceJurisdiction$;

    constructor(
        private airspaceCheckService: AirspaceCheckService,
        private logging: FlyFreelyLoggingService
    ) {
        this.airspaceJurisdiction$ =
            airspaceCheckService.airspaceJurisdiction$.pipe(
                nullSafeDistinctUntilKeyChanged('id')
            );

        combineLatest([this.airspaceCheckService.ready$, this.currentState$])
            // Using debounce time here because often the flight area and airspace check command update at the same time
            // The debounce prevents the airspace check from firing twice in those cases by waiting for everything to update.
            // Debounce 50 seems to sometimes be just not enough if there's something slowing down the system, but 100 seems to work well.
            // We also map and distinct the parameters to eliminate unneeded rechecks.
            .pipe(
                debounceTime(100),
                map(([_, state]) => ({
                    flightArea: state.flightArea,
                    parameters: {
                        airspaceHeight: state.parameters.airspaceHeight,
                        airspaceDate: state.parameters.airspaceDate,
                        airspaceDuration: state.parameters.airspaceDuration,
                        airspaceRuleset: state.parameters.airspaceRuleset
                    }
                })),
                distinctUntilChanged(deepEqual),
                takeUntilDestroyed()
            )
            .subscribe(relevantState => {
                const command = computeAirspaceCheckCommand(
                    relevantState.parameters
                );
                if (command == null || relevantState.flightArea == null) {
                    return;
                }
                this.tryAirspaceCheck(relevantState.flightArea, command);
            });

        // When the timezone from the check changes, then if automatic
        // time then just switch the timezone (display only),
        // otherwise retain the numerical time and rerun the check
        this.airspaceCheckService.result$
            .pipe(
                filter(r => r != null),
                withLatestFrom(this.currentState$),
                takeUntilDestroyed()
            )
            .subscribe(([result, state]) => {
                if (result.timeZone === state.parameters.airspaceTimeZone) {
                    return;
                }

                if (state.automaticTime) {
                    this.currentStateSource.next(
                        patchState({
                            parameters: { airspaceTimeZone: result.timeZone }
                        })
                    );
                } else {
                    const localTime = fromTimestamp(
                        state.parameters.airspaceDate!,
                        state.parameters.airspaceTimeZone
                    );
                    const destinationTime = toTimestamp(
                        localTime,
                        result.timeZone
                    );
                    this.currentStateSource.next(
                        patchState({
                            parameters: {
                                airspaceDate: new Date(destinationTime),
                                airspaceTimeZone: result.timeZone
                            }
                        })
                    );
                }
            });
    }

    ngOnDestroy() {
        this.currentStateSource.complete();
        this.isInPastSource.complete();
    }

    setup(
        organisationId: number,
        initialValues: Partial<AirspaceCheckerParameters>,
        deferredParameters: DeferredParameters
    ) {
        this.organisationId = organisationId;
        this.deferredParameters = deferredParameters;
        this.setParameters(
            initialValues,
            initialValues.airspaceDate != null ? false : undefined
        );
    }

    /**
     * Check whether all required parameters for a valid airspace check are available and that a check is possible
     * Handles invalid/missing parameters by resetting the airspace check service and entering the correct error state
     */
    private tryAirspaceCheck(
        flightArea: GeoJSON.Polygon | GeoJSON.Point,
        partialCommand: AirspaceCheckCommandWithoutLocation
    ) {
        if (flightArea == null) {
            return;
        }

        // Next check that the current flight area has a valid jurisdiction that supports airspace checks
        const jurisdiction =
            this.airspaceCheckService.findJurisdictionForFlightArea(flightArea);

        if (
            jurisdiction == null ||
            jurisdiction.airspaceCheckSupport ===
                AirspaceJurisdictionDto.AirspaceCheckSupport.NONE
        ) {
            return;
        }

        const jurisdictionIdentifier = jurisdiction?.identifier;
        if (
            jurisdictionIdentifier == null ||
            jurisdiction.identifier !== jurisdictionIdentifier
        ) {
            this.logging.warn('Unexpectedly got a jurisdiction mismatch');
            return;
        }

        // In some jurisdictions without full airspace check permission the start time can come through as null
        // In those cases fall back to the current time for the initial check
        if (partialCommand.startTime == null) {
            partialCommand.startTime = moment().toISOString();
            partialCommand.endTime = moment(partialCommand.startTime)
                .add(1, 'hour')
                .toISOString();
        }

        // If there's a valid location and jurisdiction with valid rulesets, do the airspace check
        const airspaceCheckCommand: AirspaceCheckCommand = {
            ...partialCommand,
            location: flightArea,
            jurisdiction: jurisdictionIdentifier
        };

        this.airspaceCheckService.startSingleCheck(
            airspaceCheckCommand,
            null,
            jurisdiction,
            {
                id: null,
                name: 'Flight Area',
                availableActions: { canDelete: false, canEdit: false },
                type: 'MISSION',
                organisationId: this.organisationId,
                features: [
                    {
                        name: 'Flight Area',
                        type: 'FLIGHT_AREA',
                        geometry: flightArea
                    }
                ]
            }
        );
    }

    setJurisdiction(jurisdiction: AirspaceJurisdictionDto | null) {
        this.airspaceCheckService.airspaceJurisdiction = jurisdiction;
        if (
            jurisdiction == null ||
            jurisdiction.identifier == null ||
            jurisdiction.identifier.length === 0
        ) {
            this.currentStateSource.next(
                patchState({ parameters: { airspaceRuleset: null } })
            );
            // this.noValidJurisdiction(); FIXME
        } else {
            const airspaceRulesetLookup = jurisdiction.rulesetList.filter(
                r => r.identifier != null && r.identifier.length > 0
            );
            const initialRuleset =
                airspaceRulesetLookup.find(
                    r =>
                        r.identifier ===
                        this.deferredParameters?.rulesetIdentifier
                ) ?? airspaceRulesetLookup[0];

            // Initialise the airspaceRuleset with a valid value
            this.currentStateSource.next(
                mutateState(state => {
                    if (
                        airspaceRulesetLookup.includes(
                            state.parameters?.airspaceRuleset
                        )
                    ) {
                        return state;
                    }

                    if (state.parameters?.airspaceRuleset == null) {
                        return {
                            ...state,
                            parameters: {
                                ...state?.parameters,
                                airspaceRuleset: initialRuleset
                            }
                        };
                    }

                    const airspaceRuleset =
                        airspaceRulesetLookup.find(
                            r => r.id === state.parameters.airspaceRuleset.id
                        ) ?? initialRuleset;
                    return {
                        ...state,
                        parameters: { ...state?.parameters, airspaceRuleset }
                    };
                })
            );
        }
    }

    setFlightArea(flightArea: Polygon | Point | null) {
        this.currentStateSource.next(
            mutateState(state => ({
                ...state,
                flightArea,
                parameters: {
                    ...state.parameters,
                    airspaceDate: state.automaticTime
                        ? new Date()
                        : state.parameters.airspaceDate
                }
            }))
        );
    }

    setTimeZone(timeZone: string | null) {
        this.currentStateSource.next(
            patchState({ parameters: { airspaceTimeZone: timeZone } })
        );
    }

    setParameters(
        updatedParameters: Partial<AirspaceCheckerParameters>,
        automaticTime: boolean | undefined = undefined
    ) {
        this.currentStateSource.next(
            patchState({ parameters: updatedParameters, automaticTime })
        );
    }
}
